import { useMemo } from "react";
import type { ChatStatus } from "ai";
import type { PromptAddon } from "@/stores/slices/prompt-context.slice";
import { PromptAddonType } from "@/stores/slices/prompt-context.slice";
import { imagineStore } from "@/stores/imagine.store";

type UsePromptSubmitStateArgs = {
  addons: Array<PromptAddon>;
  trimmedPrompt: string;
  chatStatus: ChatStatus | undefined;
  isGenerating: boolean;
};

export function usePromptSubmitState({
  addons,
  trimmedPrompt,
  chatStatus,
  isGenerating,
}: UsePromptSubmitStateArgs) {
  const previewUrl = imagineStore.use.previewState.previewUrl();
  const isReadonly = imagineStore.use.isReadonly();
  const { hasUploadingAddons } = useMemo(() => {
    return summarizeAddons(addons);
  }, [addons]);

  const hasSandboxInstrumented = imagineStore.use.sandboxState.instrumented();

  const hasExceededUsageLimit =
    imagineStore.use.billing.hasExceededUsageLimit();

  const hasPendingMessage = trimmedPrompt.length > 0;
  const isSubmitDisabled =
    chatStatus === "submitted" ||
    hasUploadingAddons ||
    isReadonly ||
    (previewUrl && !hasSandboxInstrumented) ||
    (!hasPendingMessage && !isGenerating) ||
    hasExceededUsageLimit;

  const showEmptyMessageTooltip =
    !hasPendingMessage && !isGenerating && !hasUploadingAddons;
  const isInputDisabled = isReadonly;

  return {
    hasPendingMessage,
    hasUploadingAddons,
    isSubmitDisabled,
    showEmptyMessageTooltip,
    isInputDisabled,
  };
}

function summarizeAddons(addons: Array<PromptAddon>) {
  let hasReadyAddons = false;
  let hasUploadingAddons = false;

  for (const addon of addons) {
    if (addon.type !== PromptAddonType.ATTACHMENT) {
      hasReadyAddons = true;
      continue;
    }

    hasUploadingAddons =
      hasUploadingAddons || addon.props.uploadStatus === "uploading";
    hasReadyAddons = hasReadyAddons || addon.props.uploadStatus === "success";
  }

  return {
    hasReadyAddons,
    hasUploadingAddons,
  };
}
